// Port 1:1 da assets/js/map-legend.js e assets/js/map-markers.js
// (v1 resta la fonte di verità per la parità; non divergere).
//
// Globali browser richiesti (opzionali, letti a runtime):
// - window.WSI18N (solo per la lingua in stationNetworkFullLabel; assente = italiano)
//
// Adattamenti dichiarati (nessun'altra modifica alla semantica):
// - markerParamAllowsRow: rimossi i guard `typeof !== 'undefined'` (i Set sono
//   in questo stesso modulo);
// - stationDisplayRecord: nuova firma (s, parametro, scaleFn, isRainInterval).
//   Il param `field` v1 era GIÀ inutilizzato nel corpo (parametro morto:
//   il valore arriva da getStationParamValue, il colore da scaleFn) → rimosso.
//   Il confronto v1 `scaleFn === rainIntervalColor` diventa il flag esplicito
//   `isRainInterval` (true = scaleFn ritorna direttamente una stringa colore,
//   false = scaleFn ritorna un oggetto chroma → .hex());
// - rankExtremeRecords: portata SOLO la parte pura (marca extremeRole/extremeRank
//   e ritorna i record); rimossi wsLatestExtremeRecords/wsLatestExtremeParam/
//   wsExtremesPanelOpen/renderExtremesPanelFromLatest (stato UI del pannello v1).

// ======== Set parametri (map-legend.js:1-11) ========
export const LAND_IDW_PARAMS = new Set([
    'temperatura', 'tmax', 'tmin', 'rain', 'rainrate', 'rainmonth', 'rainyear',
    'rain1h', 'rain3h', 'rain6h', 'rain12h', 'rain24h', 'rain48h', 'rain72h', 'rain96h', 'rain120h',
    'vento', 'raffica', 'umidita', 'dewPoint', 'apparent', 'humidex', 'wetbulb',
    'dewspread', 'windchill', 'pressione', 'neve', 'idro'
]);
export const MARINE_IDW_PARAMS = new Set([
    'temp_mare', 'onda', 'periodo_onda', 'direzione_onda',
    'vento_mare', 'raffica_mare', 'corrente_mare', 'livello_mare'
]);
export const THERMAL_IDW_PARAMS = new Set([...LAND_IDW_PARAMS, ...MARINE_IDW_PARAMS]);
const DERIVED_PARAM_IDS = new Set(['apparent', 'humidex', 'wetbulb', 'dewspread', 'windchill']);

// ======== Helper numerici (map-legend.js) ========
function finiteIdwValue(value) {
    if (value === null || value === undefined || value === '') return null;
    const n = Number.parseFloat(value);
    return Number.isFinite(n) ? n : null;
}

function stationFiniteValue(row, key) {
    return finiteIdwValue(row?.[key]);
}

export function isDerivedParam(parametro) {
    return DERIVED_PARAM_IDS.has(parametro);
}

// ======== Grandezze derivate (map-legend.js) ========
function saturationVaporPressureHpa(tempC) {
    return 6.112 * Math.exp((17.67 * tempC) / (tempC + 243.5));
}

function vaporPressureHpaFromHumidity(tempC, humidity) {
    if (!Number.isFinite(tempC) || !Number.isFinite(humidity)) return null;
    if (humidity < 0 || humidity > 100) return null;
    return (humidity / 100) * saturationVaporPressureHpa(tempC);
}

function dewPointFromTempHumidity(tempC, humidity) {
    if (!Number.isFinite(tempC) || !Number.isFinite(humidity) || humidity <= 0 || humidity > 100) return null;
    const gamma = Math.log(humidity / 100) + ((17.27 * tempC) / (237.7 + tempC));
    return (237.7 * gamma) / (17.27 - gamma);
}

export function stationDewPoint(row) {
    const dew = stationFiniteValue(row, 'dewPoint');
    if (dew !== null && dew > -99.9) return dew;
    const temp = stationFiniteValue(row, 'temperatura');
    const humidity = stationFiniteValue(row, 'umidita');
    return dewPointFromTempHumidity(temp, humidity);
}

function computeWetBulbStull(tempC, humidity) {
    if (!Number.isFinite(tempC) || !Number.isFinite(humidity) || humidity < 5 || humidity > 99) return null;
    return tempC * Math.atan(0.151977 * Math.sqrt(humidity + 8.313659))
        + Math.atan(tempC + humidity)
        - Math.atan(humidity - 1.676331)
        + 0.00391838 * Math.pow(humidity, 1.5) * Math.atan(0.023101 * humidity)
        - 4.686035;
}

export function computeDerivedValue(row, parametro) {
    const temp = stationFiniteValue(row, 'temperatura');
    const humidity = stationFiniteValue(row, 'umidita');
    const windKmh = stationFiniteValue(row, 'vento');

    if (parametro === 'apparent') {
        if (row?.comfort) { let c=row.comfort; if(typeof c==='string'){try{c=JSON.parse(c);}catch(_){return null;}} return c.available && Date.parse(c.valid_until)>=Date.now() ? c.apparent_temperature : null; }
        if (temp === null || temp <= -99.9 || humidity === null) return null;
        const vaporPressure = vaporPressureHpaFromHumidity(temp, humidity);
        if (vaporPressure === null) return null;
        const windMs = Math.max(0, windKmh ?? 0) / 3.6;
        return temp + (0.33 * vaporPressure) - (0.70 * windMs) - 4.0;
    }

    if (parametro === 'humidex') {
        if (temp === null || temp <= -99.9) return null;
        const dew = stationDewPoint(row);
        if (dew === null) return null;
        const e = 6.11 * Math.exp(5417.7530 * ((1 / 273.16) - (1 / (273.15 + dew))));
        return temp + (0.5555 * (e - 10));
    }

    if (parametro === 'wetbulb') {
        const wetBulb = stationFiniteValue(row, 'wetBulb');
        if (wetBulb !== null && wetBulb > -99.9) return wetBulb;
        if (temp === null || temp <= -99.9 || humidity === null) return null;
        return computeWetBulbStull(temp, humidity);
    }

    if (parametro === 'dewspread') {
        if (temp === null || temp <= -99.9) return null;
        const dew = stationDewPoint(row);
        if (dew === null) return null;
        return Math.max(0, temp - dew);
    }

    if (parametro === 'windchill') {
        const windChill = stationFiniteValue(row, 'windChill');
        if (windChill !== null && windChill > -99.9) return windChill;
        if (temp === null || temp > 10 || windKmh === null || windKmh < 4.8) return null;
        const windFactor = Math.pow(windKmh, 0.16);
        return 13.12 + (0.6215 * temp) - (11.37 * windFactor) + (0.3965 * temp * windFactor);
    }

    return null;
}

// Mappa parametro UI -> campo del record stazione (map-legend.js:1678).
export function fieldForParam(parametro) {
    switch (parametro) {
        case 'tmax': return 'tmax';
        case 'tmin': return 'tmin';
        case 'temperatura': return 'temperatura';
        case 'umidita': return 'umidita';
        case 'dewPoint': return 'dewPoint';
        case 'vento': return 'vento';
        case 'raffica':  return 'raffica';
        case 'pressione': return 'pressione';
        case 'rain': return 'pioggia';
        case 'rain1h': case 'rain3h': case 'rain6h': case 'rain12h': case 'rain24h': case 'rain48h': case 'rain72h': case 'rain96h': case 'rain120h': return 'pioggia_ultime_ore';
        case 'rainrate': return 'rain_rate';
        case 'rainmonth': return 'pioggia_mensile';
        case 'rainyear': return 'pioggia_annuale';
        case 'neve': return 'livello_neve';
        case 'idro': return 'livello_idrometrico';
        case 'temp_mare': return 'temp_mare';
        case 'onda': return 'wave_height_m';
        case 'periodo_onda': return 'wave_period_s';
        case 'direzione_onda': return 'wave_direction';
        case 'vento_mare': return 'wind';
        case 'raffica_mare': return 'wind_gust';
        case 'corrente_mare': return 'current_speed';
        case 'livello_mare': return 'idrometrico';
    }
}

// Neve fantasma dei nivometri: d'estate i sensori a ultrasuoni leggono valori
// falsi (erba/deriva termica) perché non c'è neve da misurare. Se la temperatura
// aria della stazione è chiaramente sopra lo zero, neve al suolo non può esserci
// → la consideriamo assente (dato fresco ma fisicamente impossibile).
const SNOW_MAX_AIR_TEMP = 5;
export function snowIsPlausible(row) {
    const t = finiteIdwValue(row?.temperature ?? row?.temperatura ?? row?.temp ?? row?.air_temperature);
    return !(t !== null && t > SNOW_MAX_AIR_TEMP);
}

export function getStationParamValue(row, parametro) {
    if (parametro === 'comfort') {
        let c = row?.comfort;
        if (typeof c === 'string') { try { c = JSON.parse(c); } catch (_) { return null; } }
        return c?.available && Date.parse(c.valid_until) >= Date.now() && [0,1,2].includes(c.score) ? c.score : null;
    }
    if (parametro === 'dewPoint') return stationDewPoint(row);
    if (isDerivedParam(parametro)) return computeDerivedValue(row, parametro);
    if (parametro === 'neve' && !snowIsPlausible(row)) return null;
    const field = fieldForParam(parametro);
    return field ? finiteIdwValue(row?.[field]) : null;
}

// ======== Rete stazione (map-legend.js:149) ========
const STATION_NETWORK_META = {
    cnr: { code: 'CNR', full: 'CNR – Consiglio Nazionale delle Ricerche' },
    rainwatch: { code: 'Rainwatch', full: 'Rainwatch – Rete Pluviometrica del Longano · Davis Vantage Pro2' },
    unipa: { code: 'UNIPA', full: 'HYCLIC – Università degli Studi di Palermo' },
    ws: { code: 'WS', full: 'Weather Sicily' },
    meteosicilia: { code: 'MeteoSicilia', full: 'MeteoSicilia' },
    meteomessina: { code: 'MeteoMessina.it', full: 'MeteoMessina.it' },
    proag: { code: 'ProAg', full: 'Protezione Civile Agrigento' },
    meteolicata: { code: 'MeteoLicata.it', full: 'MeteoLicata.it — Stazione meteo di Roberto La Placa' },
    cms: { code: 'Centro Meteorologico Siciliano', full: 'Centro Meteorologico Siciliano' },
    cisar_ag: { code: 'CISAR Agrigento', full: 'CISAR Agrigento' },
    inaf: { code: 'INAF', full: 'Istituto Nazionale di Astrofisica' },
    bess: { code: 'BESS', full: 'Progetto BESS – INGV' },
    meteocaltanissetta: { code: 'MeteoCaltanissetta', full: 'MeteoCaltanissetta' },
    drpc: { code: 'DRPC', full: 'DRPC Sicilia' },
    arpacal: { code: 'ARPACAL', full: 'ARPACAL Calabria' },
    sias: { code: 'SIAS', full: 'SIAS' },
    metar: { code: 'METAR', full: 'METAR' },
    mareografica: { code: 'ISPRA', full: 'ISPRA mareografico' }
};

function stationNetworkKey(row) {
    const category = String(row?.network_category || '').trim().toLowerCase();
    if (STATION_NETWORK_META[category]) return category;

    const raw = String(row?.rete || row?.tipo || row?.network || '').trim();
    const normalized = raw.toLowerCase();
    if (normalized === 'rainwatch' || Number(row?.officialFlag ?? row?.official) === 13) return 'rainwatch';
    if (normalized.includes('arpacal')) return 'arpacal';
    if (normalized.includes('drpc')) return 'drpc';
    if (normalized.includes('sias')) return 'sias';
    if (normalized.includes('inaf')) return 'inaf';
    if (normalized === 'bess') return 'bess';
    if (normalized.includes('meteocaltanissetta') || normalized.includes('meteo caltanissetta')) return 'meteocaltanissetta';
    if (normalized.includes('meteomessina') || normalized.includes('meteo messina')) return 'meteomessina';
    if (normalized.includes('proag') || normalized.includes('protezione civile agrigento')) return 'proag';
    if (normalized.includes('cms')) return 'cms';
    if (normalized === 'metar') return 'metar';
    if (['mareografica', 'mareografico', 'ondametrica', 'tad_server'].includes(normalized)) return 'mareografica';
    if (Number(row?.officialFlag ?? row?.official) === 5) return 'meteosicilia';
    if (Number(row?.officialFlag ?? row?.official) === 4) return 'cms';
    if (Number(row?.officialFlag ?? row?.official) === 7) return 'cisar_ag';
    if (Number(row?.officialFlag ?? row?.official) === 8) return 'inaf';
    if (Number(row?.officialFlag ?? row?.official) === 12) return 'bess';
    if (Number(row?.officialFlag ?? row?.official) === 9) return 'meteocaltanissetta';
    if (Number(row?.officialFlag ?? row?.official) === 10) return 'meteomessina';
    if (Number(row?.officialFlag ?? row?.official) === 11) return 'proag';
    if (normalized.includes('cisar')) return 'cisar_ag';
    if (normalized.includes('meteosicilia')) return 'meteosicilia';
    if (normalized === 'ws' || normalized.includes('weather sicily')) return 'ws';

    return 'ws';
}

// (map-legend.js:178-186)
export function stationNetworkBadgeLabel(row) {
    const meta = STATION_NETWORK_META[stationNetworkKey(row)] || STATION_NETWORK_META.ws;
    const code = meta.code;
    return (window.WSI18N && WSI18N.lang === 'en') ? `${code} network` : `Rete ${code}`;
}

export function stationNetworkFullLabel(row) {
    const meta = STATION_NETWORK_META[stationNetworkKey(row)] || STATION_NETWORK_META.ws;
    if (window.WSI18N && WSI18N.lang === 'en') {
        return meta.full === meta.code ? `${meta.code} network` : meta.full;
    }
    return meta.full === meta.code ? `Rete ${meta.code}` : meta.full;
}

// ======== Formattazione (map-legend.js:1772) ========
function formatSmartValue(v, decimals = 1) {
    if (!Number.isFinite(v)) return '';
    return Number.isInteger(v) ? String(v) : v.toFixed(decimals);
}

export function formatValue(parametro, v){
    if (!Number.isFinite(v)) return '';

    if (parametro === 'comfort') return ['OK','MOD','DIS'][v] || '';
    if (['temperatura','temp_mare','dewPoint','tmax','tmin','apparent','wetbulb','dewspread','windchill'].includes(parametro))
        return formatSmartValue(v, 1);

    if (parametro === 'humidex')
        return formatSmartValue(v, 1);

    if (parametro === 'umidita')
        return formatSmartValue(v, 1);

    if (parametro === 'rain' || parametro === 'rainrate' || parametro === 'rainmonth' || parametro === 'rainyear' || /^rain(?:1|3|6|12|24|48|72|96|120)h$/.test(parametro))
        return formatSmartValue(v, 1);

    if (parametro === 'vento' || parametro === 'raffica' || parametro === 'vento_mare' || parametro === 'raffica_mare')
        return formatSmartValue(v, 1);

    if (parametro === 'onda')
        return formatSmartValue(v, 2);

    if (parametro === 'periodo_onda')
        return formatSmartValue(v, 1);

    if (parametro === 'direzione_onda')
        return formatSmartValue(v, 0);

    if (parametro === 'corrente_mare')
        return formatSmartValue(v, 2);

    if (parametro === 'livello_mare')
        return formatSmartValue(v, 2);

    if (parametro === 'neve') return formatSmartValue(v, 1);
    if (parametro === 'idro') return formatSmartValue(v, 2);

    if (parametro === 'pressione')
        return formatSmartValue(v, 1);

    return String(v);
}

// ======== Da map-markers.js ========
export function finiteValue(value) {
    if (value === null || value === undefined || value === '') return null;
    const n = parseFloat(value);
    return Number.isFinite(n) ? n : null;
}

export function chartUnitForParam(parametro) {
    if (['temperatura','tmax','tmin','dewPoint','apparent','wetbulb','dewspread','windchill','temp_mare'].includes(parametro)) return '°C';
    if (parametro === 'umidita') return '%';
    if (['vento','raffica'].includes(parametro)) return 'km/h';
    if (parametro === 'rainrate') return 'mm/h';
    if (['rain','rainmonth','rainyear'].includes(parametro) || /^rain(?:1|3|6|12|24|48|72|96|120)h$/.test(parametro)) return 'mm';
    if (parametro === 'pressione') return 'hPa';
    return '';
}

// Parametri con marker "estremo" (map-markers.js:1199-1209)
export const EXTREME_BOTH_PARAMS = new Set([
    'temperatura', 'umidita', 'pressione', 'dewPoint', 'apparent',
    'wetbulb', 'dewspread', 'windchill', 'idro', 'temp_mare',
    'corrente_mare', 'livello_mare'
]);
export const EXTREME_MAX_ONLY_PARAMS = new Set([
    'tmax', 'rain', 'rainrate', 'rainmonth', 'rainyear', 'vento', 'raffica',
    'neve', 'humidex', 'onda', 'periodo_onda', 'vento_mare',
    'raffica_mare'
]);
export const EXTREME_MIN_ONLY_PARAMS = new Set(['tmin']);

export function validStationValue(parametro, raw) {
    if (['umidita','rain','rainrate','rainmonth','rainyear'].includes(parametro) || /^rain(?:1|3|6|12|24|48|72|96|120)h$/.test(parametro)) return Number.isFinite(raw) && raw >= 0;
    if (['temperatura','tmax','tmin','dewPoint','apparent','wetbulb','windchill'].includes(parametro)) return Number.isFinite(raw) && raw > -99.9 && raw < 90;
    if (parametro === 'humidex') return Number.isFinite(raw) && raw > -30 && raw < 80;
    if (parametro === 'dewspread') return Number.isFinite(raw) && raw >= 0 && raw < 80;
    if (['vento','raffica'].includes(parametro)) return Number.isFinite(raw) && raw >= 0;
    if (parametro === 'pressione') return Number.isFinite(raw) && raw >= 850 && raw <= 1100;
    if (parametro === 'neve') return Number.isFinite(raw) && raw >= 0;
    if (parametro === 'idro') return Number.isFinite(raw);
    if (parametro === 'temp_mare') return Number.isFinite(raw) && raw > -5 && raw < 45;
    if (parametro === 'livello_mare') return Number.isFinite(raw) && raw > -10 && raw < 10;
    return Number.isFinite(raw);
}

export function markerRowIsMarine(row) {
    const network = String(row?.network_category || row?.source_network || row?.type || row?.tipo || row?.network || row?.ente || '').toLowerCase();
    return network.includes('mare') || network.includes('boe') || network.includes('boa') || network.includes('ispra');
}

export function markerParamAllowsRow(parametro, row) {
    const isMarineParam = MARINE_IDW_PARAMS.has(parametro);
    const isLandParam = LAND_IDW_PARAMS.has(parametro);
    if (isMarineParam) return markerRowIsMarine(row);
    if (isLandParam && parametro !== 'temperatura') return !markerRowIsMarine(row);
    return true;
}

export function stationHasMetric(s, parametro) {
    if (isDerivedParam(parametro)) return computeDerivedValue(s, parametro) !== null;
    if (parametro === 'temperatura') return s.has_temperatura !== false && s.temperatura !== null && s.temperatura !== undefined && s.temperatura !== '';
    if (parametro === 'tmax') return s.has_tmax !== false && s.tmax !== null && s.tmax !== undefined && s.tmax !== '';
    if (parametro === 'tmin') return s.has_tmin !== false && s.tmin !== null && s.tmin !== undefined && s.tmin !== '';
    if (parametro === 'dewPoint') return stationDewPoint(s) !== null;
    return true;
}

// Soglia unica "online" <2h, identica a live e alla v1 (map-markers.js:1866):
// freschezza valutata su age_minutes (calcolato dall'API a query-time), NON
// sul timestamp col clock del browser; fallback al flag is_stale del server.
const STATION_FRESH_MAX_AGE_MIN = 120;
export function isStaleStation(station) {
    const age = finiteValue(station?.age_minutes);
    if (age !== null) return age > STATION_FRESH_MAX_AGE_MIN;
    const value = station?.is_stale;
    return value === true || value === 1 || String(value || '').toLowerCase() === 'true';
}

// NOTA firma vs v1 (map-markers.js:1784): il param v1 `field` non era mai
// usato nel corpo (parametro morto) ed è stato rimosso; il confronto identità
// `scaleFn === rainIntervalColor` è sostituito dal flag `isRainInterval`
// (la scala pioggia a intervalli ritorna già una stringa colore, le altre
// scale chroma richiedono .hex()).
export function stationDisplayRecord(s, parametro, scaleFn, isRainInterval) {
    if (isStaleStation(s)) return null;
    if (!markerParamAllowsRow(parametro, s)) return null;
    if (!stationHasMetric(s, parametro)) return null;
    const lat = finiteValue(s.lat);
    const lon = finiteValue(s.lng);
    const raw = getStationParamValue(s, parametro);
    if (lat === null || lon === null || raw === null || !validStationValue(parametro, raw)) return null;

    const color = isRainInterval ? scaleFn(raw) : scaleFn(raw).hex();
    return { id: String(s.id), lat, lon, raw, color, text: formatValue(parametro, raw), station: s };
}

export function isLowerExtremeParam(parametro) {
    return ['tmin', 'dewPoint', 'idro', 'dewspread', 'windchill'].includes(parametro);
}

export function extremeMarkerMode(parametro) {
    if (EXTREME_MIN_ONLY_PARAMS.has(parametro)) return 'min';
    if (EXTREME_MAX_ONLY_PARAMS.has(parametro)) return 'max';
    if (EXTREME_BOTH_PARAMS.has(parametro)) return 'both';
    return 'none';
}

export function shouldSkipExtremeValue(parametro, value) {
    if (!Number.isFinite(value)) return true;
    if (['rain','rainrate','rainmonth','rainyear','vento','raffica','neve'].includes(parametro) && value === 0) return true;
    return false;
}

// Parte PURA di rankExtremeRecords (map-markers.js:1852): marca sui record
// extremeRole ('max'/'min'/'both') ed extremeRank e li ritorna. Rimosso lo
// stato UI v1 del pannello estremi (wsLatestExtremeRecords/wsLatestExtremeParam/
// wsExtremesPanelOpen/renderExtremesPanelFromLatest).
export function rankExtremeRecords(records, parametro) {
    records.forEach(rec => {
        rec.extremeRank = 0;
        rec.extremeRole = '';
    });

    const candidates = records.filter(rec => !shouldSkipExtremeValue(parametro, rec.raw));
    const mode = extremeMarkerMode(parametro);
    if (candidates.length && mode !== 'none') {
        const maxRec = mode === 'min'
            ? null
            : candidates.reduce((best, rec) => !best || rec.raw > best.raw ? rec : best, null);
        const minRec = mode === 'max'
            ? null
            : candidates.reduce((best, rec) => !best || rec.raw < best.raw ? rec : best, null);

        if (maxRec) {
            maxRec.extremeRole = 'max';
            maxRec.extremeRank = 1;
        }
        if (minRec) {
            if (minRec.extremeRole === 'max') minRec.extremeRole = 'both';
            else minRec.extremeRole = 'min';
            minRec.extremeRank = 1;
        }
    }

    return records;
}
